import { useMemo, useState } from "react";
import {
  GetMarketsSummary200Response,
  InstrumentTypeResponse,
} from "../../codegen-api";
import { IMarket } from "../../contexts/MarketContext";
import { useGetMarketSummary } from "../../hooks/api/marketSummary/useGetMarketSummary";
import usePersistentState from "../../hooks/usePersistentState";
import { useGetIndexHistories } from "../../hooks/api/indexHistories/useGetIndexHistories";
import { useGetSpotStatistics } from "../../hooks/api/statistics/useGetSpotStatistics";
import { useGetMarkets } from "../../contexts/MarketInstrumentContext/useGetMarkets";
import { isInstrumentTypeEnabled } from "../../config/features";
import { ISectionData, MarketFiltersEnum } from "./CommandModal.types";
import { computeSectionsData } from "./CommandModal.sections";
import { getMarketFilterOptions } from "./CommandModal.helpers";

export function useMarketsData(
  market: IMarket,
  watchlist: IMarket[],
  t: (key: string) => string,
  tTradingType: (key: string) => string,
  /** Spot statistics are polled only while the palette is open. */
  isOpen = true
) {
  const { preferredMarketFilter: rawMarketFilter, setPreferredMarketFilter } =
    usePersistentState();
  // Fallback to ALL_MARKETS if the stored filter is stale/invalid -- or is a
  // category chip this build no longer offers (no perps: no category chips).
  const marketFilter = getMarketFilterOptions().includes(
    rawMarketFilter as MarketFiltersEnum
  )
    ? (rawMarketFilter as MarketFiltersEnum)
    : MarketFiltersEnum.ALL_MARKETS;

  // Which verticals this build offers comes from the feature-flag registry
  // (registry default -> chain override -> env override), not a chain-id list.
  const isPerpEnabled = isInstrumentTypeEnabled(
    InstrumentTypeResponse.Perpetual
  );
  const isSpotEnabled = isInstrumentTypeEnabled(InstrumentTypeResponse.Spot);

  // `/markets-summary` is the perp catalogue's summary; a build without perps
  // does not fetch it.
  const { data: marketsSummary, mutate: mutateMarketSummary } =
    useGetMarketSummary(!isPerpEnabled);

  // TEMPFIX: fallback use unique assets from /markets
  const { data: marketsData } = useGetMarkets(
    undefined,
    InstrumentTypeResponse.Perpetual,
    !isPerpEnabled
  );

  const { data: spotMarketsData } = useGetMarkets(
    undefined,
    InstrumentTypeResponse.Spot,
    !isSpotEnabled
  );

  const uniqueAssets = useMemo(
    () =>
      Array.from(
        new Set(marketsData?.map((s) => s.underlying_asset) || [])
      ).sort(),
    [marketsData]
  );

  const uniqueSpotAssets = useMemo(
    () =>
      Array.from(
        new Set(spotMarketsData?.map((s) => s.underlying_asset) || [])
      ).sort(),
    [spotMarketsData]
  );

  const { data: spotStatistics } = useGetSpotStatistics(
    uniqueSpotAssets,
    isSpotEnabled && isOpen
  );

  const marketMetaByAsset = useMemo(
    () =>
      new Map(
        (marketsData || []).map((m) => [
          m.underlying_asset,
          {
            displayName: m.display_name,
            category: m.market_category,
          },
        ])
      ),
    [marketsData]
  );

  // Fallback when markets summary is slow
  const marketsSummaryData = useMemo(() => {
    if (!marketsSummary) {
      const fallbackSummaries: GetMarketsSummary200Response = {
        summaries: uniqueAssets.map((a) => {
          const mkt = marketsData?.find((d) => a === d.underlying_asset);
          return {
            asset: a,
            index_price: mkt?.index_price || "",
            index_daily_change: "",
            option_info: {
              // "open_interest"?: {
              //   // 'calls'?: string;
              //   // 'puts'?: string;
              //   "total": "0"
              // },
              mark_price: mkt?.mark_price || "",
              mark_daily_change: "",
              price_step: mkt?.price_step || "",
              amount_step: mkt?.amount_step || "",
            },
            perpetual_info: {
              // 'open_interest'?: GetMarketsSummary200ResponseSummariesInnerPerpetualInfoOpenInterest;
              mark_price: mkt?.mark_price || "",
              mark_daily_change: "",
              // 'pre_launch'?: boolean;
              // 'daily_volume'?: string;
              price_step: mkt?.price_step || "",
              amount_step: mkt?.amount_step || "",
            },
          };
        }),
      };
      return fallbackSummaries;
    }
    return marketsSummary;
  }, [marketsData, marketsSummary, uniqueAssets]);

  const { data: assetIndexHistories, mutate: mutateIndexHistories } =
    useGetIndexHistories(uniqueAssets);

  const [marketsSummaryLastUpdatedMillis, setMarketsSummaryLastUpdatedMillis] =
    useState(Date.now());

  const [searchText, setSearchText] = useState("");
  const [highlightedMarketIndex, setHighlightedMarketIndex] = useState<{
    sectionIndex: number;
    dataIndex: number;
  }>({
    sectionIndex: 0,
    dataIndex: 0,
  });

  // Every summary, with its market's display name and category attached. The
  // search itself happens in computeSectionsData, as one ranking over perp
  // AND spot rows (see marketSearch).
  const searchResults = useMemo(
    () =>
      (marketsSummaryData?.summaries || []).map((summary) => {
        const meta = marketMetaByAsset.get(summary.asset);
        return {
          ...summary,
          display_name: meta?.displayName,
          market_category: meta?.category,
        };
      }),
    [marketMetaByAsset, marketsSummaryData?.summaries]
  );

  const sectionsData: ISectionData[] = useMemo(
    () =>
      computeSectionsData({
        searchResults,
        watchlist,
        market,
        marketFilter,
        assetIndexHistories,
        t,
        tTradingType,
        searchText,
        uniqueSpotAssets,
        spotMarketsData,
        spotStatistics,
      }),
    [
      assetIndexHistories?.assets_history,
      market.asset,
      market.derivative,
      marketFilter,
      searchResults,
      searchText,
      spotMarketsData,
      spotStatistics,
      uniqueSpotAssets,
      t,
      tTradingType,
      watchlist,
    ]
  );

  return {
    marketFilter,
    setPreferredMarketFilter,
    mutateMarketSummary,
    mutateIndexHistories,
    marketsSummaryLastUpdatedMillis,
    setMarketsSummaryLastUpdatedMillis,
    searchText,
    setSearchText,
    highlightedMarketIndex,
    setHighlightedMarketIndex,
    sectionsData,
  };
}
